// src/pages/crm/prospects.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import '../../styles/crm/prospects.css';
import '../../styles/crm/relances.css';
import ProspectRelances from '../../components/crm/ProspectRelances';
import RelanceModal from '../../components/crm/RelanceModal';
import ProgressScore from '../../components/crm/ProgressScore';
import SessionSelectionModal from '../../components/crm/SessionSelectionModal';
import {
  getProspects,
  getProspect,
  createProspect,
  updateProspect,
  deleteProspect,
  deleteMultipleProspects,
  convertToEtudiant,
} from '../../services/crm/prospectsService';
import { createRelance } from '../../services/crm/relancesService';
import api from '../../services/api';
import { validateField, validateAll, NIVEAUX_ANNEE_SECONDAIRE, NIVEAU_SECONDAIRE_ANCIEN } from '../../script/crm/validation';
import ImportProspectsModal from './ImportProspectsModal';

/* ══════════════════════════════════════════════════════════
   CONSTANTES
══════════════════════════════════════════════════════════ */
const STATUT_COLORS = {
  'Très intéressé':  { bg: 'rgba(34,197,94,.13)',   color: '#166534', border: 'rgba(34,197,94,.35)'  },
  'Intéressé moyen': { bg: 'rgba(251,191,36,.16)',   color: '#854d0e', border: 'rgba(251,191,36,.40)' },
  'Pas intéressé':   { bg: 'rgba(148,163,184,.15)',  color: '#475569', border: 'rgba(148,163,184,.40)' },
  'Perdu':           { bg: 'rgba(229,62,62,.10)',    color: '#c0392b', border: 'rgba(229,62,62,.30)'  },
};

const STATUT_GENERE_CONFIG = {
  'Très intéressé':  { bg: 'rgba(34,197,94,.18)',  color: '#14532d', border: 'rgba(34,197,94,.50)',  icon: '🔥' },
  'Intéressé moyen': { bg: 'rgba(251,191,36,.20)',  color: '#713f12', border: 'rgba(251,191,36,.55)', icon: '📈' },
  'Pas intéressé':   { bg: 'rgba(148,163,184,.18)', color: '#334155', border: 'rgba(148,163,184,.50)', icon: '❄️' },
  'Perdu':           { bg: 'rgba(229,62,62,.14)',   color: '#991b1b', border: 'rgba(229,62,62,.45)',  icon: '🚫' },
};

// ✅ Fonction pour extraire la catégorie parente à partir du statut IA dynamique
const getStatutCategorie = (statutGenere) => {
  if (!statutGenere) return null;
  if (statutGenere.includes('Très intéressé')) return 'Très intéressé';
  if (statutGenere.includes('Intéressé moyen')) return 'Intéressé moyen';
  if (statutGenere.includes('Pas intéressé')) return 'Pas intéressé';
  if (statutGenere.includes('Perdu') || statutGenere.includes('prospect perdu')) return 'Perdu';
  return null;
};

const SOURCES            = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'Google', 'Site web', 'Recommandation', 'Appel entrant', 'Autre'];
const PAYS_LIST          = ['Tunisie', 'France', 'Algérie', 'Maroc', 'Belgique', 'Canada', 'Autre'];
const GENRE_LIST         = ['Homme', 'Femme', 'Autre'];
const NIVEAU_ETUDES_LIST = ['Primaire', ...NIVEAUX_ANNEE_SECONDAIRE, 'Baccalauréat', 'Universitaire'];
const DIPLOME_LIST       = ['Bac', 'Licence', 'Master', 'Autre'];

// ✅ NOUVEAUX SEUILS POUR LE SCORE IA
const SCORE_FILTER_OPTIONS = [
  { value: 'all', label: 'Tous les scores' },
  { value: 'high', label: '🔥Très intéressé (≥76%)', minScore: 76, maxScore: 100 },
  { value: 'medium', label: '🟡Intéressé moyen (40-75%)', minScore: 40, maxScore: 75 },
  { value: 'low', label: '❄️Pas intéressé (5-39%)', minScore: 5, maxScore: 39 },
  { value: 'lost', label: '🚫Perdu (<5%)', minScore: 0, maxScore: 4 },
];

const FORM_FIELDS = [
  'nom', 'prenom', 'email', 'tel', 'ville', 'pays',
  'dateNaissance', 'genre', 'niveauEtudes', 'diplomeObtenu',
  'source', 'formation', 'niveau', 'modePreference',
  'canalContact', 'commentaires', 'statut',
];

/* ══════════════════════════════════════════════════════════
   SOUS-COMPOSANTS FORMULAIRE
══════════════════════════════════════════════════════════ */
const F = ({ label, name, type = 'text', placeholder = '', autoComplete, fd, set, errors = {} }) => (
  React.createElement('div', { className: `pf-group${errors[name] ? ' pf-group--error' : ''}` },
    React.createElement('label', null, label),
    React.createElement('input', {
      type: type,
      value: fd[name] || '',
      placeholder: placeholder,
      autoComplete: autoComplete || 'off',
      onChange: e => set(name, e.target.value),
      className: errors[name] ? 'input-error' : ''
    }),
    errors[name] && React.createElement('span', { className: 'pf-error-msg' },
      React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
      ' ',
      errors[name]
    )
  )
);

const S = ({ label, name, options, fd, set, errors = {} }) => (
  React.createElement('div', { className: `pf-group${errors[name] ? ' pf-group--error' : ''}` },
    React.createElement('label', null, label),
    React.createElement('select', {
      value: fd[name] || '',
      onChange: e => set(name, e.target.value),
      className: errors[name] ? 'input-error' : ''
    },
      React.createElement('option', { value: '' }, '— Sélectionner —'),
      options.map(o => React.createElement('option', { key: o, value: o }, o))
    ),
    errors[name] && React.createElement('span', { className: 'pf-error-msg' },
      React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
      ' ',
      errors[name]
    )
  )
);

const T = ({ label, name, placeholder = '', fd, set, errors = {} }) => (
  React.createElement('div', { className: `pf-group pf-full${errors[name] ? ' pf-group--error' : ''}` },
    React.createElement('label', null, label),
    React.createElement('textarea', {
      rows: 3,
      value: fd[name] || '',
      placeholder: placeholder,
      onChange: e => set(name, e.target.value),
      className: errors[name] ? 'input-error' : ''
    }),
    errors[name] && React.createElement('span', { className: 'pf-error-msg' },
      React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
      ' ',
      errors[name]
    )
  )
);

/* ══════════════════════════════════════════════════════════
   FORMATION MULTI-DROPDOWN
══════════════════════════════════════════════════════════ */
const FormationMultiDropdown = ({ values = [], onChange, formations = [], error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const normalizedValues = values.map(v => String(v));
  
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = formations.filter(f =>
    f.label.toLowerCase().includes(search.toLowerCase())
  );

  const selected = formations.filter(f => normalizedValues.includes(String(f.id)));

  const handleToggle = (formationId) => {
    const idStr = String(formationId);
    if (normalizedValues.includes(idStr)) {
      onChange(values.filter(id => String(id) !== idStr));
    } else {
      onChange([...values, formationId]);
    }
  };

  const handleClear = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (selected.length === 0) return 'Sélectionner une ou plusieurs formations';
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} formations sélectionnées`;
  };

  return React.createElement('div', { className: `pf-group fd-wrap${error ? ' pf-group--error' : ''}`, ref: ref },
    React.createElement('label', null, 'Formation(s) souhaitée(s)'),
    React.createElement('button', {
      type: 'button',
      className: `fd-trigger${error ? ' input-error' : ''}${open ? ' fd-trigger--open' : ''}`,
      onClick: () => setOpen(v => !v)
    },
      React.createElement('span', { className: `fd-trigger-text${selected.length === 0 ? ' fd-placeholder' : ''}` },
        getDisplayText()
      ),
      selected.length > 0 && React.createElement('span', { className: 'fd-selected-badge' },
        selected.length,
        React.createElement('i', { className: 'fa-solid fa-check', style: { marginLeft: '4px', fontSize: '10px' } })
      ),
      React.createElement('i', { className: `fa-solid fa-chevron-${open ? 'up' : 'down'} fd-chevron` })
    ),
    open && React.createElement('div', { className: 'fd-dropdown fd-multi-dropdown' },
      React.createElement('div', { className: 'fd-search-wrap' },
        React.createElement('i', { className: 'fa-solid fa-magnifying-glass fd-search-icon' }),
        React.createElement('input', {
          ref: inputRef,
          type: 'text',
          className: 'fd-search-input',
          placeholder: 'Rechercher une formation...',
          value: search,
          onChange: e => setSearch(e.target.value)
        }),
        values.length > 0 && React.createElement('button', {
          className: 'fd-clear-btn',
          onClick: handleClear,
          title: 'Effacer la sélection'
        },
          React.createElement('i', { className: 'fa-solid fa-xmark' })
        )
      ),
      React.createElement('div', { className: 'fd-list' },
        filtered.length === 0 ?
          React.createElement('div', { className: 'fd-empty' }, 'Aucune formation trouvée') :
          filtered.map(f => {
            const isSelected = normalizedValues.includes(String(f.id));
            return React.createElement('div', {
              key: f.id,
              className: `fd-item fd-multi-item${isSelected ? ' fd-item--selected' : ''}`,
              onClick: () => handleToggle(String(f.id))
            },
              React.createElement('div', { className: `fd-multi-checkbox${isSelected ? ' fd-multi-checkbox--checked' : ''}` },
                isSelected && React.createElement('i', { className: 'fa-solid fa-check' })
              ),
              React.createElement('i', { className: 'fa-solid fa-tag fd-item-icon' }),
              f.label,
              React.createElement('span', { className: 'fd-item-duree' }, f.duree)
            );
          })
      ),
      selected.length > 0 && React.createElement('div', { className: 'fd-selected-info' },
        React.createElement('i', { className: 'fa-solid fa-circle-check' }),
        ' ', selected.length, ' formation', selected.length > 1 ? 's' : '', ' sélectionnée', selected.length > 1 ? 's' : ''
      )
    ),
    error && React.createElement('span', { className: 'pf-error-msg' },
      React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
      ' ',
      error
    )
  );
};

/* ══════════════════════════════════════════════════════════
   FILTRE FORMATION DROPDOWN
══════════════════════════════════════════════════════════ */
const FormationFilterDropdown = ({ value, onChange, formations = [] }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = formations.filter(f =>
    f.label.toLowerCase().includes(search.toLowerCase())
  );

  const selected = formations.find(f => String(f.id) === String(value));

  const handleSelect = (f) => {
    onChange(String(f.id));
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange('Toutes');
    setOpen(false);
    setSearch('');
  };

  return React.createElement('div', { className: 'filter-dropdown-wrap', ref: ref },
    React.createElement('button', {
      type: 'button',
      className: `filter-sel filter-formation-btn${open ? ' filter-open' : ''}${value !== 'Toutes' ? ' filter-active' : ''}`,
      onClick: () => setOpen(v => !v)
    },
      React.createElement('span', { className: 'filter-btn-text' },
        value !== 'Toutes' && selected ? selected.label : 'Toutes les formations'
      ),
      React.createElement('i', { className: `fa-solid fa-chevron-${open ? 'up' : 'down'} filter-chevron` })
    ),
    open && React.createElement('div', { className: 'filter-dropdown' },
      React.createElement('div', { className: 'filter-search-wrap' },
        React.createElement('i', { className: 'fa-solid fa-magnifying-glass filter-search-icon' }),
        React.createElement('input', {
          ref: inputRef,
          type: 'text',
          className: 'filter-search-input',
          placeholder: 'Rechercher une formation...',
          value: search,
          onChange: e => setSearch(e.target.value)
        }),
        search && React.createElement('button', {
          className: 'filter-search-clear',
          onClick: () => setSearch('')
        },
          React.createElement('i', { className: 'fa-solid fa-xmark' })
        )
      ),
      React.createElement('div', { className: 'filter-list' },
        React.createElement('div', {
          className: `filter-item${value === 'Toutes' ? ' filter-item--selected' : ''}`,
          onClick: handleClear
        },
          React.createElement('i', { className: 'fa-solid fa-list filter-item-icon' }),
          'Toutes les formations'
        ),
        filtered.length === 0
          ? React.createElement('div', { className: 'filter-empty' }, 'Aucune formation trouvée')
          : filtered.map(f => React.createElement('div', {
              key: f.id,
              className: `filter-item${String(f.id) === String(value) ? ' filter-item--selected' : ''}`,
              onClick: () => handleSelect(f)
            },
              React.createElement('i', { className: 'fa-solid fa-tag filter-item-icon' }),
              f.label,
              React.createElement('span', { className: 'filter-item-duree' }, f.duree)
            ))
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   FORMULAIRE PROSPECT
══════════════════════════════════════════════════════════ */
const ProspectForm = ({ initial, formRef, FORMATIONS }) => {
  const defaults = {
    nom: '', prenom: '', email: '', tel: '', ville: '', pays: '',
    dateNaissance: '', genre: '', niveauEtudes: '', diplomeObtenu: '',
    formations: [], niveau: '', modePreference: '',
    canalContact: '', source: '', statut: '', commentaires: '',
  };

  const normalizeFormations = (formations) => {
    if (!formations || !Array.isArray(formations)) return [];
    return formations.map(id => String(id));
  };

  const [fd, setFd] = useState({ 
    ...defaults, 
    ...(initial || {}),
    formations: normalizeFormations(initial?.formations || initial?.formations_ids || [])
  });
  const [errors, setErrors] = useState({});

  const getDiplomeOptions = (niveauEtudes) => {
    if (niveauEtudes === 'Baccalauréat' || niveauEtudes === NIVEAU_SECONDAIRE_ANCIEN) return ['Bac'];
    if (niveauEtudes === 'Universitaire') return DIPLOME_LIST;
    return [];
  };

  const shouldShowDiplome = (niveauEtudes) => {
    return ['Baccalauréat', 'Universitaire', NIVEAU_SECONDAIRE_ANCIEN].includes(niveauEtudes);
  };

  // Aucun champ n'est obligatoire, le diplôme non plus.
  const isDiplomeRequired = () => {
    return false;
  };

  formRef.current = {
    data: fd,
    triggerValidation: () => {
      const { errors: errs, isValid } = validateAll(fd);
      setErrors(errs);
      return isValid;
    },
    setApiErrors: (apiErrors) => setErrors(prev => ({ ...prev, ...apiErrors })),
  };

  const set = (field, value) => {
    if (field === 'niveauEtudes') {
      // Le diplôme dépend du niveau : « Baccalauréat » implique le Bac, les
      // autres niveaux repartent sans diplôme.
      const diplomeObtenu = value === 'Baccalauréat' ? 'Bac' : '';
      const suivant = { ...fd, niveauEtudes: value, diplomeObtenu };
      setFd(prev => ({ ...prev, niveauEtudes: value, diplomeObtenu }));
      setErrors(prev => ({
        ...prev,
        niveauEtudes: validateField('niveauEtudes', value, suivant),
        diplomeObtenu: validateField('diplomeObtenu', diplomeObtenu, suivant),
      }));
    } else {
      setFd(prev => ({ ...prev, [field]: value }));
      setErrors(prev => ({ 
        ...prev, 
        [field]: validateField(field, value, { ...fd, [field]: value }) 
      }));
    }

  };

  return React.createElement('form', { className: 'pf-body', autoComplete: 'on', onSubmit: e => e.preventDefault() },
    React.createElement('div', { className: 'pf-section-title' },
      React.createElement('i', { className: 'fa-solid fa-user' }),
      ' Informations personnelles'
    ),
    React.createElement('div', { className: 'pf-grid' },
      React.createElement(F, { label: 'Nom', name: 'nom', placeholder: 'Ben Ali', fd: fd, set: set, errors: errors }),
      React.createElement(F, { label: 'Prénom', name: 'prenom', placeholder: 'Sami', fd: fd, set: set, errors: errors }),
      React.createElement(F, { label: 'Email ', name: 'email', type: 'email', placeholder: 'email@exemple.com', autoComplete: 'email', fd: fd, set: set, errors: errors }),
      React.createElement(F, { label: 'Téléphone', name: 'tel', placeholder: '+216 XX XXX XXX', autoComplete: 'tel', fd: fd, set: set, errors: errors }),
      React.createElement(F, { label: 'Ville', name: 'ville', placeholder: 'Tunis', fd: fd, set: set, errors: errors }),
      React.createElement(S, { label: 'Pays', name: 'pays', options: PAYS_LIST, fd: fd, set: set, errors: errors })
    ),
    React.createElement('div', { className: 'pf-section-title', style: { marginTop: '18px' } },
      React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
      ' Profil académique'
    ),
    React.createElement('div', { className: 'pf-grid' },
      React.createElement(F, { label: 'Date de naissance', name: 'dateNaissance', type: 'date', fd: fd, set: set, errors: errors }),
      React.createElement(S, { label: 'Genre', name: 'genre', options: GENRE_LIST, fd: fd, set: set, errors: errors }),
      React.createElement('div', { className: `pf-group${errors['niveauEtudes'] ? ' pf-group--error' : ''}` },
        React.createElement('label', null, 'Niveau d\'études'),
        React.createElement('select', {
          value: fd.niveauEtudes || '',
          onChange: e => set('niveauEtudes', e.target.value),
          className: errors['niveauEtudes'] ? 'input-error' : ''
        },
          React.createElement('option', { value: '' }, '— Sélectionner —'),
          (fd.niveauEtudes === NIVEAU_SECONDAIRE_ANCIEN ? [...NIVEAU_ETUDES_LIST, NIVEAU_SECONDAIRE_ANCIEN] : NIVEAU_ETUDES_LIST)
            .map(o => React.createElement('option', { key: o, value: o }, o))
        ),
        errors['niveauEtudes'] && React.createElement('span', { className: 'pf-error-msg' },
          React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
          ' ',
          errors['niveauEtudes']
        )
      ),
      shouldShowDiplome(fd.niveauEtudes) && React.createElement('div', { className: `pf-group${errors['diplomeObtenu'] ? ' pf-group--error' : ''}` },
        React.createElement('label', null, 
          'Diplôme obtenu',
          isDiplomeRequired(fd.niveauEtudes) && React.createElement('span', { style: { color: '#e53e3e', marginLeft: '4px' } }, '*')
        ),
        React.createElement('select', {
          value: fd.diplomeObtenu || '',
          onChange: e => set('diplomeObtenu', e.target.value),
          className: errors['diplomeObtenu'] ? 'input-error' : ''
        },
          React.createElement('option', { value: '' }, '— Sélectionner —'),
          getDiplomeOptions(fd.niveauEtudes).map(o => React.createElement('option', { key: o, value: o }, o))
        ),
        errors['diplomeObtenu'] && React.createElement('span', { className: 'pf-error-msg' },
          React.createElement('i', { className: 'fa-solid fa-circle-exclamation' }),
          ' ',
          errors['diplomeObtenu']
        )
      )
    ),
    React.createElement('div', { className: 'pf-section-title', style: { marginTop: '18px' } },
      React.createElement('i', { className: 'fa-solid fa-briefcase' }),
      ' Informations commerciales'
    ),
    React.createElement('div', { className: 'pf-grid' },
      React.createElement(S, { label: 'Source — Comment avez-vous connu 4C Lab ?', name: 'source', options: SOURCES, fd: fd, set: set, errors: errors }),
      React.createElement(FormationMultiDropdown, {
        values: fd.formations || [],
        onChange: (selectedIds) => set('formations', selectedIds),
        formations: FORMATIONS,
        error: errors['formations']
      }),
      React.createElement(S, { label: 'Niveau estimé', name: 'niveau', options: ['Débutant', 'Intermédiaire', 'Avancé'], fd: fd, set: set, errors: errors }),
      React.createElement(S, { label: 'Mode préféré', name: 'modePreference', options: ['Présentiel', 'En ligne', 'Hybride'], fd: fd, set: set, errors: errors }),
      React.createElement(S, { label: 'Canal de contact préféré', name: 'canalContact', options: ['Téléphone', 'Email', 'WhatsApp'], fd: fd, set: set, errors: errors }),
      React.createElement(T, { label: 'Commentaires', name: 'commentaires', placeholder: 'Notes, observations...', fd: fd, set: set, errors: errors })
    ),
    React.createElement('div', { className: 'pf-section-title', style: { marginTop: '18px' } },
      React.createElement('i', { className: 'fa-solid fa-chart-line' }),
      ' Suivi du prospect'
    ),
    React.createElement('div', { className: 'pf-grid' },
      React.createElement(S, { label: "Niveau d'intérêt", name: 'statut', options: ['Très intéressé', 'Intéressé moyen', 'Pas intéressé', 'Perdu'], fd: fd, set: set, errors: errors })
    )
  );
};

/* ══════════════════════════════════════════════════════════
   DRAWER PANEL
══════════════════════════════════════════════════════════ */
const DrawerPanel = ({ drawerOpen, drawerMode, drawerTarget, closeDrawer, saveDrawer, saving, formRef, FORMATIONS }) => {
  if (!drawerOpen) return null;
  const isEdit = drawerMode === 'edit';
  return React.createElement(React.Fragment, null,
    React.createElement('div', { className: 'drawer-overlay', onClick: closeDrawer }),
    React.createElement('div', { className: 'drawer-panel open' },
      React.createElement('div', { className: `drawer-header ${isEdit ? 'drawer-header-edit' : ''}` },
        React.createElement('div', { className: 'drawer-header-left' },
          React.createElement('i', { className: `fa-solid ${isEdit ? 'fa-pen' : 'fa-user-plus'}` }),
          React.createElement('span', null, isEdit ? 'Modifier le prospect' : 'Ajouter un prospect')
        ),
        React.createElement('button', { className: 'drawer-close', onClick: closeDrawer },
          React.createElement('i', { className: 'fa-solid fa-xmark' })
        )
      ),
      React.createElement('div', { className: 'drawer-body' },
        React.createElement(ProspectForm, { key: isEdit ? drawerTarget?.id : 'new', initial: isEdit ? drawerTarget : null, formRef: formRef, FORMATIONS: FORMATIONS })
      ),
      React.createElement('div', { className: 'drawer-footer' },
        React.createElement('button', { className: 'btn btn-cancel', onClick: closeDrawer, disabled: saving }, 'Annuler'),
        React.createElement('button', { className: `btn ${isEdit ? 'btn-update' : 'btn-save'}`, onClick: saveDrawer, disabled: saving },
          saving ? React.createElement(React.Fragment, null,
            React.createElement('i', { className: 'fa-solid fa-spinner fa-spin' }),
            ' Enregistrement…'
          ) : React.createElement(React.Fragment, null,
            React.createElement('i', { className: `fa-solid ${isEdit ? 'fa-floppy-disk' : 'fa-plus'}` }),
            ' ',
            isEdit ? 'Enregistrer' : 'Ajouter'
          )
        )
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   MODALE SUPPRESSION (unitaire)
══════════════════════════════════════════════════════════ */
const DeleteModal = ({ showDeleteModal, deleteTarget, closeDelete, confirmDelete }) => {
  if (!showDeleteModal || !deleteTarget) return null;
  const sc = STATUT_COLORS[deleteTarget.statut] || {};
  return React.createElement('div', { className: 'modal-overlay show', onClick: e => { if (e.target === e.currentTarget) closeDelete(); } },
    React.createElement('div', { className: 'modal-suppr' },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'center', paddingTop: '28px' } },
        React.createElement('div', { className: 'suppr-icon-wrap' },
          React.createElement('i', { className: 'fa-solid fa-trash', style: { fontSize: '26px', color: '#ef4444' } })
        )
      ),
      React.createElement('div', { style: { padding: '16px 24px', textAlign: 'center' } },
        React.createElement('h2', { style: { fontSize: '18px', fontWeight: '700', marginBottom: '14px', color: '#1e293b' } }, 'Supprimer le prospect'),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', textAlign: 'left' } },
          React.createElement('div', { style: { background: '#336699', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '13px', flexShrink: 0 } },
            deleteTarget.prenom[0], deleteTarget.nom[0]
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontWeight: '600', color: '#1e293b' } }, deleteTarget.prenom, ' ', deleteTarget.nom),
            React.createElement('div', { style: { fontSize: '11.5px', color: '#94a3b8' } }, deleteTarget.email)
          ),
          React.createElement('span', { className: 'badge', style: { marginLeft: 'auto', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` } }, deleteTarget.statut)
        ),
        React.createElement('div', { className: 'suppr-warning' },
          React.createElement('i', { className: 'fa-solid fa-triangle-exclamation', style: { flexShrink: 0 } }),
          React.createElement('span', null, 'Cette action est ', React.createElement('strong', null, 'irréversible'), '. Le prospect sera définitivement supprimé.')
        )
      ),
      React.createElement('div', { style: { padding: '12px 24px 20px', display: 'flex', gap: '10px' } },
        React.createElement('button', { className: 'btn btn-cancel', style: { flex: 1 }, onClick: closeDelete },
          React.createElement('i', { className: 'fa-solid fa-xmark' }), ' Annuler'
        ),
        React.createElement('button', { className: 'btn btn-suppr-confirm', style: { flex: 1 }, onClick: confirmDelete },
          React.createElement('i', { className: 'fa-solid fa-trash' }), ' Confirmer'
        )
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   MODALE SUPPRESSION EN MASSE
══════════════════════════════════════════════════════════ */
const BulkDeleteModal = ({ show, count, onCancel, onConfirm, deleting }) => {
  if (!show) return null;
  return React.createElement('div', { className: 'modal-overlay show', onClick: e => { if (e.target === e.currentTarget && !deleting) onCancel(); } },
    React.createElement('div', { className: 'modal-suppr' },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'center', paddingTop: '28px' } },
        React.createElement('div', { className: 'suppr-icon-wrap' },
          React.createElement('i', { className: 'fa-solid fa-trash-can', style: { fontSize: '26px', color: '#ef4444' } })
        )
      ),
      React.createElement('div', { style: { padding: '16px 24px', textAlign: 'center' } },
        React.createElement('h2', { style: { fontSize: '18px', fontWeight: '700', marginBottom: '10px', color: '#1e293b' } },
          'Supprimer ', count, ' prospect', count > 1 ? 's' : ''
        ),
        React.createElement('p', { style: { fontSize: '13.5px', color: '#64748b', marginBottom: '16px' } },
          'Vous êtes sur le point de supprimer ', React.createElement('strong', null, count, ' prospect', count > 1 ? 's' : ''), ' sélectionné', count > 1 ? 's' : '', '.'
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.20)', borderRadius: '10px', padding: '12px 18px', marginBottom: '14px' } },
          React.createElement('div', { style: { background: '#ef4444', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '16px', flexShrink: 0 } }, count),
          React.createElement('span', { style: { fontSize: '13.5px', color: '#7f1d1d', fontWeight: '500' } },
            'prospect', count > 1 ? 's' : '', ' seront supprimé', count > 1 ? 's' : '', ' définitivement'
          )
        ),
        React.createElement('div', { className: 'suppr-warning' },
          React.createElement('i', { className: 'fa-solid fa-triangle-exclamation', style: { flexShrink: 0 } }),
          React.createElement('span', null, 'Cette action est ', React.createElement('strong', null, 'irréversible'), '. Toutes les données associées seront perdues.')
        )
      ),
      React.createElement('div', { style: { padding: '12px 24px 20px', display: 'flex', gap: '10px' } },
        React.createElement('button', { className: 'btn btn-cancel', style: { flex: 1 }, onClick: onCancel, disabled: deleting },
          React.createElement('i', { className: 'fa-solid fa-xmark' }), ' Annuler'
        ),
        React.createElement('button', { className: 'btn btn-suppr-confirm', style: { flex: 1 }, onClick: onConfirm, disabled: deleting },
          deleting ?
            React.createElement(React.Fragment, null,
              React.createElement('i', { className: 'fa-solid fa-spinner fa-spin' }),
              ' Suppression…'
            ) :
            React.createElement(React.Fragment, null,
              React.createElement('i', { className: 'fa-solid fa-trash' }),
              ' Supprimer ', count
            )
        )
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   BARRE D'ACTIONS EN MASSE
══════════════════════════════════════════════════════════ */
const BulkActionBar = ({ count, onDelete, onClear }) => {
  if (count === 0) return null;
  return React.createElement('div', { className: 'bulk-action-bar' },
    React.createElement('div', { className: 'bulk-action-info' },
      React.createElement('div', { className: 'bulk-action-count' }, count),
      React.createElement('span', null, 'prospect', count > 1 ? 's' : '', ' sélectionné', count > 1 ? 's' : '')
    ),
    React.createElement('div', { className: 'bulk-action-btns' },
      React.createElement('button', { className: 'bulk-btn-clear', onClick: onClear },
        React.createElement('i', { className: 'fa-solid fa-xmark' }), ' Désélectionner tout'
      ),
      React.createElement('button', { className: 'bulk-btn-delete', onClick: onDelete },
        React.createElement('i', { className: 'fa-solid fa-trash' }), ' Supprimer la sélection'
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   MODALE CONFIRMATION CONVERSION
══════════════════════════════════════════════════════════ */
const ConfirmConvertModal = ({ show, detailTarget, selectedForms, convertData, FORMATIONS, onCancel, onConfirm, converting }) => {
  if (!show || !detailTarget) return null;
  const { documentsFournis = [] } = convertData;
  return React.createElement('div', { className: 'modal-overlay show', onClick: e => { if (e.target === e.currentTarget && !converting) onCancel(); } },
    React.createElement('div', { className: 'modal-suppr', style: { maxWidth: '430px' } },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'center', paddingTop: '28px' } },
        React.createElement('div', { style: { width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(26,107,74,.12)', border: '2px solid rgba(26,107,74,.30)', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
          React.createElement('i', { className: 'fa-solid fa-graduation-cap', style: { fontSize: '24px', color: '#1A6B4A' } })
        )
      ),
      React.createElement('div', { style: { padding: '16px 24px', textAlign: 'center' } },
        React.createElement('h2', { style: { fontSize: '18px', fontWeight: '700', marginBottom: '6px', color: '#1e293b' } }, 'Confirmer la conversion'),
        React.createElement('p', { style: { fontSize: '13px', color: '#64748b', marginBottom: '16px' } }, 'Cette action est ', React.createElement('strong', null, 'irréversible'), '. Le prospect sera supprimé et un compte étudiant sera créé automatiquement.'),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', textAlign: 'left' } },
          React.createElement('div', { style: { background: '#1A6B4A', borderRadius: '8px', width: '38px', height: '38px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '13px' } },
            detailTarget.prenom[0], detailTarget.nom[0]
          ),
          React.createElement('div', { style: { textAlign: 'left' } },
            React.createElement('div', { style: { fontWeight: '600', color: '#1e293b' } }, detailTarget.prenom, ' ', detailTarget.nom),
            React.createElement('div', { style: { fontSize: '11.5px', color: '#94a3b8' } }, detailTarget.email)
          ),
          React.createElement('span', { style: { marginLeft: 'auto', fontSize: '11px', fontWeight: '600', background: 'rgba(26,107,74,.12)', color: '#1A6B4A', border: '1px solid rgba(26,107,74,.30)', borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap' } }, '→ Étudiant ', convertData.statutEtudiant)
        ),
        React.createElement('div', { style: { background: 'rgba(51,204,255,.06)', border: '1px solid rgba(51,204,255,.25)', borderRadius: '8px', padding: '10px 14px', textAlign: 'left' } },
          React.createElement('div', { style: { fontSize: '11px', color: '#1A7A99', fontWeight: '700', marginBottom: '6px' } },
            React.createElement('i', { className: 'fa-solid fa-book-open', style: { marginRight: '5px' } }),
            selectedForms.length, ' formation(s) sélectionnée(s)'
          ),
          selectedForms.map(id => { const f = FORMATIONS.find(x => x.id === id); return f ? React.createElement('div', { key: id, style: { fontSize: '12.5px', color: '#1e293b', marginBottom: '2px' } }, '• ', f.label, ' ', React.createElement('span', { style: { color: '#94a3b8', fontSize: '11px' } }, '(', f.duree, ')')) : null; })
        ),
        documentsFournis.length > 0 && React.createElement('div', { style: { background: 'rgba(120,80,200,.06)', border: '1px solid rgba(120,80,200,.22)', borderRadius: '8px', padding: '10px 14px', textAlign: 'left', marginTop: '8px' } },
          React.createElement('div', { style: { fontSize: '11px', color: '#5B2D8E', fontWeight: '700', marginBottom: '6px' } },
            React.createElement('i', { className: 'fa-solid fa-file-lines', style: { marginRight: '5px' } }),
            documentsFournis.length, ' document(s) fourni(s)'
          ),
          React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '5px' } },
            documentsFournis.map(doc => React.createElement('span', { key: doc, style: { background: 'rgba(120,80,200,.12)', color: '#5B2D8E', border: '1px solid rgba(120,80,200,.30)', borderRadius: '5px', padding: '2px 8px', fontSize: '11.5px', fontWeight: '600' } }, doc))
          )
        )
      ),
      React.createElement('div', { style: { padding: '12px 24px 20px', display: 'flex', gap: '10px' } },
        React.createElement('button', { className: 'btn btn-cancel', style: { flex: 1 }, onClick: onCancel, disabled: converting },
          React.createElement('i', { className: 'fa-solid fa-xmark' }), ' Annuler'
        ),
        React.createElement('button', { className: 'btn btn-save', style: { flex: 1, background: '#1A6B4A', borderColor: '#1A6B4A' }, onClick: onConfirm, disabled: converting },
          converting ?
            React.createElement(React.Fragment, null,
              React.createElement('i', { className: 'fa-solid fa-spinner fa-spin' }),
              ' Conversion…'
            ) :
            React.createElement(React.Fragment, null,
              React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
              ' Confirmer'
            )
        )
      )
    )
  );
};

/* ══════════════════════════════════════════════════════════
   TOAST
══════════════════════════════════════════════════════════ */
const Toast = ({ toast }) => {
  if (!toast.show) return null;
  return React.createElement('div', { className: `toast ${toast.type}` },
    React.createElement('i', { className: `fa-solid ${toast.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}` }),
    toast.message
  );
};

/* ══════════════════════════════════════════════════════════
   CONVERT FORMATION PICKER
══════════════════════════════════════════════════════════ */
const ConvertFormationPicker = ({ formations = [], selected = [], onToggle }) => {
  const [search, setSearch] = useState('');
  const filtered = formations.filter(f =>
    f.label.toLowerCase().includes(search.toLowerCase())
  );
  return React.createElement('div', { className: 'cfp-wrap' },
    React.createElement('div', { className: 'cfp-search-wrap' },
      React.createElement('i', { className: 'fa-solid fa-magnifying-glass cfp-search-icon' }),
      React.createElement('input', {
        type: 'text',
        className: 'cfp-search-input',
        placeholder: 'Rechercher une formation...',
        value: search,
        onChange: e => setSearch(e.target.value)
      }),
      search && React.createElement('button', { className: 'cfp-clear-btn', onClick: () => setSearch('') },
        React.createElement('i', { className: 'fa-solid fa-xmark' })
      )
    ),
    React.createElement('div', { className: 'cfp-list' },
      filtered.length === 0 ?
        React.createElement('div', { className: 'cfp-empty' }, 'Aucune formation trouvée') :
        filtered.map(f => {
          const checked = selected.includes(f.id);
          return React.createElement('div', {
            key: f.id,
            className: `cfp-item${checked ? ' cfp-item--checked' : ''}`,
            onClick: () => onToggle(f.id)
          },
            React.createElement('div', { className: `cfp-checkbox${checked ? ' cfp-checkbox--checked' : ''}` },
              checked && React.createElement('i', { className: 'fa-solid fa-check cfp-check-icon' })
            ),
            React.createElement('span', { className: 'cfp-label' }, f.label),
            React.createElement('span', { className: 'dur-tag' }, f.duree)
          );
        })
    ),
    selected.length > 0 && React.createElement('div', { className: 'cfp-counter' },
      React.createElement('i', { className: 'fa-solid fa-circle-check' }),
      ' ', selected.length, ' formation', selected.length > 1 ? 's' : '', ' sélectionnée', selected.length > 1 ? 's' : ''
    )
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  CONFIGURATION DES NOTES POUR L'AFFICHAGE (AJOUT DE "PERDU")
// ══════════════════════════════════════════════════════════════════════════════
const getNotesConfig = (notes) => {
  const configs = {
    chaud: { label: 'Très intéressé', cls: 'badge-notes-chaud', icon: '🔥' },
    moyen: { label: 'Intéressé moyen', cls: 'badge-notes-moyen', icon: '🤔' },
    froid: { label: 'Pas intéressé', cls: 'badge-notes-froid', icon: '❄️' },
    perdu_definitif: { label: 'Perdu définitivement', cls: 'badge-notes-perdu', icon: '🚫' },
  };
  return configs[notes];
};

/* ══════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
══════════════════════════════════════════════════════════ */
const Prospects = () => {
  const [FORMATIONS, setFORMATIONS] = useState([]);
  useEffect(() => {
    api.get('formations/')
      .then(res => setFORMATIONS(res.data.map(f => ({ id: f.id, label: f.intitule, duree: `${f.duree_totale_heures}h` }))))
      .catch(() => setFORMATIONS([]));
  }, []);

  // ── States principaux ──
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('Toutes');
  const [filterFormation, setFilterFormation] = useState('Toutes');
  const [filterScoreIA, setFilterScoreIA] = useState('all');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [sortAlpha, setSortAlpha] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageView, setPageView] = useState('list');
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null);
  const [drawerTarget, setDrawerTarget] = useState(null);
  const formRef = useRef(null);
  const PER_PAGE = 7;

  // ── États sélection en masse ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ── États conversion ──
  const [convertOpen, setConvertOpen] = useState(false);
  const [selectedForms, setSelectedForms] = useState([]);
  const [convertData, setConvertData] = useState({ statutEtudiant: 'Actif', notes: '', documentsFournis: [] });
  const [showConfirmConvert, setShowConfirmConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  
  // ✅ AJOUT - État pour le modal de sélection des sessions
  const [showSessionSelection, setShowSessionSelection] = useState(false);

  // ── États pour le modal de relance (partagé) ──
  const [relanceModalOpen, setRelanceModalOpen] = useState(false);
  const [relanceModalLoading, setRelanceModalLoading] = useState(false);

  // ── États pour l'échange spontané ──
  const [showEchangeModal, setShowEchangeModal] = useState(false);
  const [echangeType, setEchangeType] = useState('appel');
  const [echangeContenu, setEchangeContenu] = useState('');
  const [echangeNotes, setEchangeNotes] = useState('');
  const [echangeFormations, setEchangeFormations] = useState([]);
  const [echangeSaving, setEchangeSaving] = useState(false);

  // ── États pour suppression d'échange ──
  const [deletingEchange, setDeletingEchange] = useState(false);

  // ── Filtre historique des échanges ──
  const [historiqueFiltreSource, setHistoriqueFiltreSource] = useState('tous');
  const [historiquePage, setHistoriquePage] = useState(1);
  const HIST_PER_PAGE = 5;

  // ── Chargement ──
  const loadProspects = useCallback(async () => {
    setLoading(true); setApiError(null);
    try { 
      const data = await getProspects();
      setProspects(data);
    }
    catch { setApiError('Impossible de charger les prospects. Vérifiez votre connexion.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadProspects(); }, [loadProspects]);

  // Réinitialiser la sélection quand les filtres/recherche changent
  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, filterSource, filterFormation, filterScoreIA, filterDateDebut, filterDateFin, sortAlpha, currentPage]);

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3500);
  };

  const getFiltered = () => {
    let f = [...prospects];
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(p => p.nom.toLowerCase().includes(q) || p.prenom.toLowerCase().includes(q) || p.email.toLowerCase().includes(q) || (p.tel || '').includes(q));
    }
    if (filterSource !== 'Toutes') f = f.filter(p => p.source === filterSource);
    
    // ✅ CORRECTION : Filtrer par formation (ID dans le tableau formations)
    if (filterFormation !== 'Toutes') {
      const formationId = parseInt(filterFormation, 10);
      f = f.filter(p => p.formations && p.formations.includes(formationId));
    }
    
    if (filterDateDebut) f = f.filter(p => p.date && p.date >= filterDateDebut);
    if (filterDateFin) f = f.filter(p => p.date && p.date <= filterDateFin);
    
    if (filterScoreIA !== 'all') {
      const scoreFilter = SCORE_FILTER_OPTIONS.find(opt => opt.value === filterScoreIA);
      if (scoreFilter) {
        f = f.filter(p => p.scoreIA >= scoreFilter.minScore && p.scoreIA <= scoreFilter.maxScore);
      }
    }
    
    if (sortAlpha) f.sort((a, b) => a.nom.localeCompare(b.nom));
    else f.sort((a, b) => new Date(b.date) - new Date(a.date));
    return f;
  };
  
  const filtered = getFiltered();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentSlice = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  // ── Sélection en masse ──
  const currentPageIds = currentSlice.map(p => p.id);
  const isAllPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id));
  const isSomePageSelected = currentPageIds.some(id => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (isAllPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        currentPageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        currentPageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ── Suppression en masse ──
  const openBulkDelete = () => setShowBulkDeleteModal(true);
  const closeBulkDelete = () => setShowBulkDeleteModal(false);

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { deleted, errors } = await deleteMultipleProspects(ids);

      setProspects(prev => prev.filter(p => !deleted.includes(p.id)));
      clearSelection();
      closeBulkDelete();

      if (errors.length === 0) {
        showToast(`${deleted.length} prospect${deleted.length > 1 ? 's' : ''} supprimé${deleted.length > 1 ? 's' : ''} avec succès.`);
      } else {
        showToast(
          `${deleted.length} supprimé${deleted.length > 1 ? 's' : ''}, ${errors.length} erreur${errors.length > 1 ? 's' : ''}.`,
          'error'
        );
      }
    } catch {
      showToast('Erreur lors de la suppression en masse.', 'error');
      closeBulkDelete();
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Drawer ──
  const openAdd = () => { setDrawerTarget(null); setDrawerMode('add'); setDrawerOpen(true); };
  const openEdit = (id) => { const p = prospects.find(x => x.id === id); if (!p) return; setDrawerTarget(p); setDrawerMode('edit'); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setDrawerMode(null); setDrawerTarget(null); formRef.current = null; };

  const saveDrawer = async () => {
    const ref = formRef.current; if (!ref) return;
    const isValid = ref.triggerValidation();
    if (!isValid) { showToast("Veuillez corriger les erreurs avant d'enregistrer.", 'error'); return; }
    const data = ref.data;
 
    const formationIds = data.formations || [];    
    const { responsableId, ...cleanData } = data;
    setSaving(true);
    try {
      if (drawerMode === 'add') {
        const newP = await createProspect(cleanData, formationIds);
        setProspects(prev => [newP, ...prev]);
        showToast('Prospect ajouté avec succès !');
      } else {
        const updated = await updateProspect(drawerTarget.id, cleanData, formationIds);
        setProspects(prev => prev.map(p => p.id === drawerTarget.id ? updated : p));
        if (detailTarget && detailTarget.id === drawerTarget.id) setDetailTarget(updated);
        showToast('Prospect modifié avec succès !');
      }
      closeDrawer();
    } catch (err) {
      const apiData = err.response?.data;
      if (apiData && typeof apiData === 'object') {
        const FIELD_MAP = { telephone: 'tel', date_naissance: 'dateNaissance', niveau_etudes: 'niveauEtudes', diplome_obtenu: 'diplomeObtenu', responsable: 'responsableId' };
        const fieldErrors = {}; const otherMessages = [];
        Object.entries(apiData).forEach(([key, val]) => {
          const msgs = Array.isArray(val) ? val : [val];
          const reactField = FIELD_MAP[key] || key;
          if (FORM_FIELDS.includes(reactField)) fieldErrors[reactField] = msgs.join(' ');
          else otherMessages.push(msgs.join(' '));
        });
        if (Object.keys(fieldErrors).length > 0 && formRef.current?.setApiErrors) formRef.current.setApiErrors(fieldErrors);
        showToast(otherMessages.length > 0 ? otherMessages.join(' ') : Object.keys(fieldErrors).length > 0 ? 'Veuillez corriger les erreurs signalées.' : 'Une erreur est survenue.', 'error');
      } else { showToast('Une erreur est survenue.', 'error'); }
    } finally { setSaving(false); }
  };

  // ── Détail ──
  const openDetail = async (id) => {
    try {
      const p = await getProspect(id);
      setDetailTarget(p);
      setConvertOpen(false); 
      setRelanceModalOpen(false);
      
      const defaultSelectedForms = p.formations_ids || p.formations || [];
      setSelectedForms(defaultSelectedForms);
      setConvertData({ 
        statutEtudiant: 'Actif', 
        notes: '', 
        documentsFournis: [] 
      });
      setPageView('detail');
    } catch (error) {
      console.error('Erreur chargement détail:', error);
      showToast('Impossible de charger les détails du prospect.', 'error');
    }
  };

  // ── Recharger la fiche détail ──
  const refreshDetail = async (id) => {
    try {
      const p = await getProspect(id);
      setDetailTarget(p);
    } catch { /* silencieux */ }
  };
  const closeDetail = () => { setPageView('list'); setDetailTarget(null); };

  // ── Supprimer un échange ──
  const handleDeleteEchange = async (echangeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet échange ? Cette action est irréversible.')) {
      return;
    }
    try {
      setDeletingEchange(true);
      await api.delete(`prospects/${detailTarget.id}/historiques/${echangeId}/`);
      const updated = await getProspect(detailTarget.id);
      setDetailTarget(updated);
      showToast('Échange supprimé avec succès !');
    } catch (err) {
      console.error('Erreur suppression échange:', err);
      showToast('Erreur lors de la suppression de l\'échange.', 'error');
    } finally {
      setDeletingEchange(false);
    }
  };

  // ── Supprimer (unitaire) ──
  const openDelete = (id) => { const p = prospects.find(x => x.id === id); if (!p) return; setDeleteTarget(p); setShowDeleteModal(true); };
  const closeDelete = () => { setShowDeleteModal(false); setDeleteTarget(null); };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProspect(deleteTarget.id);
      setProspects(prev => prev.filter(p => p.id !== deleteTarget.id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(deleteTarget.id); return next; });
      if (detailTarget && detailTarget.id === deleteTarget.id) { setPageView('list'); setDetailTarget(null); }
      closeDelete(); showToast('Prospect supprimé.');
    } catch { showToast('Erreur lors de la suppression.', 'error'); closeDelete(); }
  };

  // ── Conversion ──
  const toggleConvert = () => {
    setConvertOpen(v => !v);
    
    if (!convertOpen && detailTarget) {
      const defaultForms = detailTarget.formations_ids || 
                           (detailTarget.formation ? [parseInt(detailTarget.formation, 10)] : []);
      setSelectedForms(defaultForms);
    }
    
    setConvertData({ statutEtudiant: 'Actif', notes: '', documentsFournis: [] });
  };
  
  const toggleForm = (id) => setSelectedForms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  
  // ✅ MODIFIÉ - Ouvre le modal de sélection de session au lieu de la confirmation directe
  const handleConvert = () => {
    // ✅ SUPPRIMÉ: La vérification du statut 'Perdu' - conversion autorisée quel que soit le statut
    
    if (!selectedForms.length) { 
      showToast('Veuillez sélectionner au moins une formation.', 'error'); 
      return; 
    }
    
    // Fermer le panneau de conversion et ouvrir le modal de sélection de session
    setConvertOpen(false);
    setShowSessionSelection(true);
  };

  // ✅ NOUVEAU - Gère la confirmation avec les sessions sélectionnées
  const handleSessionConfirm = async (formationsWithSessions) => {
    setConverting(true);
    try {
      await convertToEtudiant(detailTarget.id, {
        formations: formationsWithSessions,
        statut_etudiant: convertData.statutEtudiant,
        notes: convertData.notes,
        documents_fournis: convertData.documentsFournis,
      });
      setProspects(prev => prev.filter(p => p.id !== detailTarget.id));
      setShowSessionSelection(false);
      closeDetail();
      showToast(`🎓 ${detailTarget.prenom} ${detailTarget.nom} a été converti(e) en étudiant(e) !`);
    } catch (err) {
      const d = err.response?.data;
      showToast(d?.error || d?.detail || d?.message || 'Erreur lors de la conversion.', 'error');
    } finally {
      setConverting(false);
    }
  };

  // ── Création de relance ──
  const handleCreateRelance = async ({ dateRelance, commentaire, formationIds }) => {
    if (!detailTarget) return;
    try {
      setRelanceModalLoading(true);
      const created = await createRelance(detailTarget.id, {
        dateRelance,
        commentaire,
        formationIds: formationIds && formationIds.length > 0 
          ? formationIds 
          : (detailTarget.formations_list && detailTarget.formations_list[0]?.id ? [detailTarget.formations_list[0].id] : []),
      });
      await refreshDetail(detailTarget.id);
      showToast('✅ Relance programmée avec succès !');
      setRelanceModalOpen(false);
    } catch (err) {
      console.error('Erreur création relance :', err);
      showToast('Erreur lors de la création de la relance.', 'error');
    } finally {
      setRelanceModalLoading(false);
    }
  };

  // ── Création d'échange spontané ──
  const handleAddEchange = async () => {
    if (!detailTarget) return;
    if (!echangeContenu.trim()) {
      showToast('Veuillez saisir un résumé de l\'échange.', 'error');
      return;
    }
    try {
      setEchangeSaving(true);
      await api.post(`prospects/${detailTarget.id}/historiques/`, {
        type_echange: echangeType,
        contenu: echangeContenu,
        notes: echangeNotes,
        formations: echangeFormations,
      });
      
      const updated = await getProspect(detailTarget.id);
      setDetailTarget(updated);
      setProspects(prev => prev.map(p => 
        p.id === detailTarget.id ? updated : p
      ));
      
      setShowEchangeModal(false);
      setEchangeType('appel');
      setEchangeContenu('');
      setEchangeNotes('');
      setEchangeFormations([]);
      showToast('Échange ajouté avec succès !');
    } catch (err) {
      console.error('Erreur ajouté change:', err);
      showToast('Erreur lors de l\'ajout de l\'échange.', 'error');
    } finally {
      setEchangeSaving(false);
    }
  };

  // Ajoutez cette fonction vers la ligne ~1000, après handleSessionConfirm
  const confirmConvert = async () => {
    setConverting(true);
    try {
        await convertToEtudiant(detailTarget.id, {
            formations: selectedForms.map(id => ({ formation_id: id, session_id: null })),
            statut_etudiant: convertData.statutEtudiant,
            notes: convertData.notes,
            documents_fournis: convertData.documentsFournis,
        });
        setProspects(prev => prev.filter(p => p.id !== detailTarget.id));
        setShowConfirmConvert(false);
        closeDetail();
        showToast(`🎓 ${detailTarget.prenom} ${detailTarget.nom} a été converti(e) en étudiant(e) !`);
    } catch (err) {
        const d = err.response?.data;
        showToast(d?.error || d?.detail || d?.message || 'Erreur lors de la conversion.', 'error');
    } finally {
        setConverting(false);
    }
  };

  // Configuration des types d'échange pour l'affichage
  const getTypeConfig = (type) => {
    const configs = {
      appel:   { icon: 'fa-phone', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', label: 'Appel téléphonique' },
      email:   { icon: 'fa-envelope', color: '#3B82F6', bg: '#EFF6FF', border: '#93C5FD', label: 'Email' },
      message: { icon: 'fa-comment', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', label: 'Message WhatsApp' },
      rdv:     { icon: 'fa-calendar-check', color: '#8B5CF6', bg: '#F5F3FF', border: '#C4B5FD', label: 'Rendez-vous' },
      autre:   { icon: 'fa-circle-info', color: '#64748B', bg: '#F1F5F9', border: '#CBD5E1', label: 'Autre' },
    };
    return configs[type] || configs.autre;
  };

  const getSourceConfig = (source) => {
    const configs = {
      relance:  { label: 'Relance', cls: 'badge-source-relance', icon: '📞' },
      spontane: { label: 'Contact spontané', cls: 'badge-source-spontane', icon: '💬' },
    };
    return configs[source];
  };

  // ══════════════════════════════════════════════════════════
  // PAGE DÉTAIL
  // ══════════════════════════════════════════════════════════
  if (pageView === 'detail' && detailTarget) {
    const p = detailTarget;
    
    // Récupération des données IA
    const iaData = p.ia || {
      score: p.scoreIA || 0,
      niveau_icon: '📊',
      niveau: 'Non disponible',
      priorite: 'Moyenne',
      priorite_badge: 'badge-medium',
      action_recommandee: '📋 Planifier relance standard',
      canal_conseille: '📧 Email',
      analyse: 'Analyse en cours...',
      attention: null,
    };

    const formationsForSession = selectedForms.map(id => FORMATIONS.find(f => f.id === id)).filter(Boolean);

    return React.createElement(Layout, null,
      React.createElement('div', { className: 'det-page' },
        React.createElement('div', { className: 'det-topbar' },
          React.createElement('button', { className: 'back-btn', onClick: closeDetail },
            React.createElement('i', { className: 'fa-solid fa-arrow-left' }),
            React.createElement('span', null, 'Prospects')
          ),
          React.createElement('i', { className: 'fa-solid fa-chevron-right det-bc-sep' }),
          React.createElement('span', { className: 'det-bc-name' }, p.prenom, ' ', p.nom)
        ),
        React.createElement('div', { className: 'det-body' },
          React.createElement('div', { className: 'det-sidebar' },
            React.createElement('div', { className: 'det-sid-hero' },
              React.createElement('div', { className: 'det-sid-avatar' }, p.prenom[0], p.nom[0]),
              React.createElement('div', { className: 'det-sid-name' }, p.prenom, ' ', p.nom),
              p.statutGenere && (() => {
                const categorie = getStatutCategorie(p.statutGenere);
                const cfg = STATUT_GENERE_CONFIG[categorie] || {};
                return React.createElement('div', { style: { marginTop: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' } },
                  React.createElement('span', { style: { fontSize: '10.5px', color: '#94a3b8', fontWeight: '500', letterSpacing: '0.4px', textTransform: 'uppercase' } }, '🤖 Statut IA'),
                  React.createElement('span', {
                    className: 'badge',
                    style: { background: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}`, display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', padding: '4px 10px' },
                    title: p.statutGenere
                  }, cfg.icon, ' ', p.statutGenere)
                );
              })()
            ),
            React.createElement('div', { className: 'det-sid-fields' },
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-regular fa-envelope' }), ' E-mail'),
                React.createElement('span', { className: 'det-sid-val' }, p.email)
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-phone' }), ' Téléphone'),
                React.createElement('span', { className: 'det-sid-val' }, p.tel || '—')
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-location-dot' }), ' Ville / Pays'),
                React.createElement('span', { className: 'det-sid-val' }, [p.ville, p.pays].filter(Boolean).join(', ') || '—')
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-regular fa-calendar-days' }), ' Naissance'),
                React.createElement('span', { className: 'det-sid-val' }, p.dateNaissance || '—')
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-venus-mars' }), ' Genre'),
                React.createElement('span', { className: 'det-sid-val' }, p.genre || '—')
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-share-nodes' }), ' Source'),
                React.createElement('span', { className: 'det-sid-val' }, React.createElement('span', { className: 'src-tag' }, p.source || '—'))
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-message' }), ' Canal préféré'),
                React.createElement('span', { className: 'det-sid-val' }, p.canalContact || '—')
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-regular fa-calendar' }), ' Date création'),
                React.createElement('span', { className: 'det-sid-val' }, p.date)
              ),
              React.createElement('div', { className: 'det-sid-field' },
                React.createElement('span', { className: 'det-sid-label' }, React.createElement('i', { className: 'fa-solid fa-user-tie' }), ' Responsable'),
                React.createElement('span', { className: 'det-sid-val' }, p.responsable || '—')
              )
            ),
            React.createElement('div', { className: 'det-sid-divider' }),
            React.createElement('div', { className: 'det-sid-actions' },
              React.createElement('button', { className: 'det-action-btn det-action-convert', onClick: toggleConvert },
                React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
                convertOpen ? 'Fermer la conversion' : 'Convertir en étudiant'
              ),
              React.createElement('button', { className: 'det-action-btn det-action-relance', onClick: () => setRelanceModalOpen(true) },
                React.createElement('i', { className: 'fa-solid fa-bell' }),
                ' Ajouter une relance'
              ),
              React.createElement('button', { className: 'det-action-btn det-action-edit', onClick: () => openEdit(p.id) },
                React.createElement('i', { className: 'fa-solid fa-pen' }), ' Modifier'
              )
            )
          ),
          React.createElement('div', { className: 'det-main' },
            // ═══════════════════════════════════════════════════════════
            // CARTE ASSISTANT IA
            // ═══════════════════════════════════════════════════════════
            React.createElement('div', { className: 'det-ia-card' },
              React.createElement('div', { className: 'det-ia-header' },
                React.createElement('i', { className: 'fa-solid fa-microchip' }),
                React.createElement('span', null, 'Assistant IA'),
                React.createElement('span', { className: 'det-ia-score-badge' }, 
                  `Score: ${iaData.score}%`
                )
              ),
              React.createElement('div', { className: 'det-ia-content' },
                React.createElement('div', { className: 'det-ia-row' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-chart-line' }),
                    ' Score de conversion'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement(ProgressScore, { score: iaData.score, showBadge: false, showTooltip: false, size: 'small' })
                  )
                ),
                React.createElement('div', { className: 'det-ia-row' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-ranking-star' }),
                    ' Niveau'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement('span', { className: 'det-ia-niveau' }, 
                      iaData.niveau_icon || '📊', ' ', iaData.niveau
                    )
                  )
                ),
                React.createElement('div', { className: 'det-ia-row' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-flag' }),
                    ' Priorité'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement('span', { className: `det-ia-priorite ${iaData.priorite_badge}` },
                      iaData.priorite
                    )
                  )
                ),
               p.statutGenere && React.createElement('div', { className: 'det-ia-row' },
                React.createElement('div', { className: 'det-ia-label' }, React.createElement('i', { className: 'fa-solid fa-robot' }), ' Statut IA généré'),
                React.createElement('div', { className: 'det-ia-value' }, (() => {
                  const categorie = getStatutCategorie(p.statutGenere);
                  const cfg = STATUT_GENERE_CONFIG[categorie] || {};
                  return React.createElement('span', {
                    className: 'badge',
                    style: { background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px' },
                    title: p.statutGenere
                  }, cfg.icon, ' ', p.statutGenere);
                })()
              )
                ),
                React.createElement('div', { className: 'det-ia-row det-ia-action' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-bullhorn' }),
                    ' Action recommandée'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement('span', { className: 'det-ia-action-text' },
                      iaData.action_recommandee
                    )
                  )
                ),
                React.createElement('div', { className: 'det-ia-row' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-message' }),
                    ' Canal conseillé'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement('span', { className: 'det-ia-canal' },
                      iaData.canal_conseille
                    )
                  )
                ),
                React.createElement('div', { className: 'det-ia-row det-ia-analyse' },
  React.createElement('div', { className: 'det-ia-label' },
    React.createElement('i', { className: 'fa-solid fa-chart-simple' }),
    ' Analyse IA'
  ),
  React.createElement('div', { className: 'det-ia-value' },
    React.createElement('div', { className: 'det-ia-analyse-list' },
      (iaData.analyse || "Aucune analyse particulière.").split(' | ').map((item, idx) =>
        React.createElement('div', { key: idx, className: 'det-ia-analyse-item' },
          React.createElement('i', { className: 'fa-solid fa-circle', style: { fontSize: '6px', marginRight: '8px', verticalAlign: 'middle', color: '#33CCFF' } }),
          item
        )
      )
    )
  )
),
                iaData.attention && React.createElement('div', { className: 'det-ia-row det-ia-warning' },
                  React.createElement('div', { className: 'det-ia-label' },
                    React.createElement('i', { className: 'fa-solid fa-triangle-exclamation' }),
                    ' Attention'
                  ),
                  React.createElement('div', { className: 'det-ia-value' },
                    React.createElement('span', { className: 'det-ia-attention' },
                      iaData.attention
                    )
                  )
                )
              )
            ),
            convertOpen && React.createElement('div', { className: 'convert-box det-convert-box' },
              React.createElement('div', { className: 'convert-title' },
                React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
                ' Conversion en Étudiant',
                React.createElement('button', { className: 'det-convert-close', onClick: toggleConvert }, React.createElement('i', { className: 'fa-solid fa-xmark' }))
              ),
              React.createElement('div', { className: 'conv-section-label' },
                React.createElement('i', { className: 'fa-solid fa-book-open' }),
                ' Formation(s) suivie(s) ',
                React.createElement('span', { style: { color: '#e53e3e', marginLeft: '4px' } }, '*')
              ),
              React.createElement(ConvertFormationPicker, {
                formations: FORMATIONS,
                selected: selectedForms,
                onToggle: toggleForm
              }),
              React.createElement('div', { className: 'form-group', style: { marginTop: '12px' } },
                React.createElement('label', { className: 'form-label' }, 'Documents fournis'),
                React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginTop: '6px' } },
                  ['CIN', 'CV', 'Contrat', 'Reçu', 'RNE', 'Autres'].map(doc => React.createElement('label', { key: doc, style: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', userSelect: 'none' } },
                    React.createElement('input', {
                      type: 'checkbox',
                      checked: convertData.documentsFournis.includes(doc),
                      onChange: () => setConvertData(prev => ({ ...prev, documentsFournis: prev.documentsFournis.includes(doc) ? prev.documentsFournis.filter(d => d !== doc) : [...prev.documentsFournis, doc] })),
                      style: { width: '15px', height: '15px', cursor: 'pointer' }
                    }),
                    doc
                  ))
                )
              ),
              React.createElement('div', { className: 'form-group', style: { marginTop: '10px' } },
                React.createElement('label', { className: 'form-label' }, 'Notes / Observations'),
                React.createElement('textarea', {
                  className: 'form-control',
                  rows: 2,
                  placeholder: 'Observations, remarques sur l\'étudiant...',
                  value: convertData.notes,
                  onChange: e => setConvertData(prev => ({ ...prev, notes: e.target.value }))
                })
              ),
              React.createElement('div', { className: 'det-convert-footer' },
                React.createElement('button', { className: 'btn btn-cancel', onClick: toggleConvert }, 'Annuler'),
                React.createElement('button', { className: 'btn-confirm', style: { flex: 1 }, onClick: handleConvert },
                  React.createElement('i', { className: 'fa-solid fa-check', style: { marginRight: '5px' } }),
                  ' Suivant'
                )
              )
            ),
            React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header' },
                React.createElement('i', { className: 'fa-solid fa-user' }),
                ' Informations personnelles'
              ),
              React.createElement('div', { className: 'det-fields-grid' },
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Nom'), React.createElement('span', { className: 'det-field-val' }, p.nom)),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Prénom'), React.createElement('span', { className: 'det-field-val' }, p.prenom)),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Email'), React.createElement('span', { className: 'det-field-val' }, p.email)),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Téléphone'), React.createElement('span', { className: 'det-field-val' }, p.tel || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Ville'), React.createElement('span', { className: 'det-field-val' }, p.ville || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Pays'), React.createElement('span', { className: 'det-field-val' }, p.pays || '—'))
              )
            ),
            React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header' },
                React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
                ' Profil académique'
              ),
              React.createElement('div', { className: 'det-fields-grid' },
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Date de naissance'), React.createElement('span', { className: 'det-field-val' }, p.dateNaissance || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Genre'), React.createElement('span', { className: 'det-field-val' }, p.genre || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Niveau d\'études'), React.createElement('span', { className: 'det-field-val' }, p.niveauEtudes || '—')),
                p.diplomeObtenu && React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Diplôme obtenu'), React.createElement('span', { className: 'det-field-val' }, p.diplomeObtenu))
              )
            ),
            React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header' },
                React.createElement('i', { className: 'fa-solid fa-briefcase' }),
                ' Informations commerciales'
              ),
              React.createElement('div', { className: 'det-fields-grid' },
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Source'), React.createElement('span', { className: 'det-field-val' }, React.createElement('span', { className: 'src-tag' }, p.source || '—'))),
                React.createElement('div', { className: 'det-field' }, 
                  React.createElement('span', { className: 'det-field-label' }, 'Formation(s) souhaitée(s)'), 
                  React.createElement('div', { className: 'det-field-val' }, 
                    p.formations_list && p.formations_list.length > 0 ?
                      React.createElement('div', { className: 'formations-list' },
                        p.formations_list.map(f => 
                          React.createElement('span', { key: f.id, className: 'formation-badge' }, 
                            f.intitule,
                            f.duree && React.createElement('span', { className: 'formation-duree' }, f.duree)
                          )
                        )
                      ) :
                      React.createElement('span', { style: { color: '#94a3b8' } }, '—')
                  )
                ),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Niveau estimé'), React.createElement('span', { className: 'det-field-val' }, p.niveau || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Mode préféré'), React.createElement('span', { className: 'det-field-val' }, p.modePreference || '—')),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Canal de contact'), React.createElement('span', { className: 'det-field-val' }, p.canalContact || '—'))
              )
            ),
            React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header' },
                React.createElement('i', { className: 'fa-solid fa-chart-line' }),
                ' Suivi du prospect'
              ),
              React.createElement('div', { className: 'det-fields-grid' },
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Date de création'), React.createElement('span', { className: 'det-field-val' }, p.date)),
                React.createElement('div', { className: 'det-field' }, React.createElement('span', { className: 'det-field-label' }, 'Responsable'), React.createElement('span', { className: 'det-field-val' }, p.responsable || '—'))
              )
            ),
            p.commentaires ? React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header' },
                React.createElement('i', { className: 'fa-solid fa-note-sticky' }),
                ' Commentaires'
              ),
              React.createElement('div', { className: 'notes-box' }, p.commentaires)
            ) : null,
            React.createElement('div', { className: 'det-section-card' },
              React.createElement('div', { className: 'det-section-header', style: { justifyContent: 'space-between' } },
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                  React.createElement('i', { className: 'fa-solid fa-clock-rotate-left' }),
                  ' Historique des échanges',
                  React.createElement('span', { style: { fontSize: '11.5px', fontWeight: '700', background: 'rgba(51,204,255,.12)', color: '#1A7A99', border: '1px solid rgba(51,204,255,.25)', borderRadius: '12px', padding: '2px 10px' } },
                    p.historiques?.length || 0, ' échange', (p.historiques?.length || 0) > 1 ? 's' : ''
                  )
                ),
                React.createElement('button', {
                  onClick: () => setShowEchangeModal(true),
                  style: { background: '#EFF6FF', color: '#3B82F6', border: '1.5px solid #BFDBFE', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }
                },
                  React.createElement('i', { className: 'fa-solid fa-plus' }),
                  ' Nouvel échange'
                )
              ),
              React.createElement('p', { style: { fontSize: '12.5px', color: '#94a3b8', marginBottom: '12px', marginTop: '-4px' } },
                'Consultez l\'historique complet ou filtrez les relances selon leur source.'
              ),
              React.createElement('div', { className: 'hist-filter-bar' },
                React.createElement('button', {
                  className: `hist-filter-btn hist-filter-btn--tous${historiqueFiltreSource === 'tous' ? ' hist-filter-btn--active' : ''}`,
                  onClick: () => { setHistoriqueFiltreSource('tous'); setHistoriquePage(1); }
                },
                  React.createElement('i', { className: 'fa-solid fa-list' }),
                  ' Tous les échanges',
                  React.createElement('span', { className: 'hist-filter-count' },
                    p.historiques?.length || 0
                  )
                ),
                React.createElement('button', {
                  className: `hist-filter-btn hist-filter-btn--spontane${historiqueFiltreSource === 'spontane' ? ' hist-filter-btn--active' : ''}`,
                  onClick: () => { setHistoriqueFiltreSource('spontane'); setHistoriquePage(1); }
                },
                  React.createElement('i', { className: 'fa-solid fa-user' }),
                  ' Relances : Contact spontané',
                  React.createElement('span', { className: 'hist-filter-count' },
                    p.historiques?.filter(h => h.source === 'spontane').length || 0
                  )
                ),
                React.createElement('button', {
                  className: `hist-filter-btn hist-filter-btn--relance${historiqueFiltreSource === 'relance' ? ' hist-filter-btn--active' : ''}`,
                  onClick: () => { setHistoriqueFiltreSource('relance'); setHistoriquePage(1); }
                },
                  React.createElement('i', { className: 'fa-solid fa-rotate' }),
                  ' Relances : Relance',
                  React.createElement('span', { className: 'hist-filter-count' },
                    p.historiques?.filter(h => h.source === 'relance').length || 0
                  )
                )
              ),
              React.createElement('div', { className: 'echanges-timeline' },
                (() => {
                  const historiquesFiltrés = historiqueFiltreSource === 'tous'
                    ? (p.historiques || [])
                    : historiqueFiltreSource === 'spontane'
                      ? (p.historiques || []).filter(h => h.source === 'spontane')
                      : (p.historiques || []).filter(h => h.source === 'relance');

                  if (historiquesFiltrés.length === 0) {
                    return React.createElement('div', { className: 'empty-state', style: { padding: '30px', textAlign: 'center' } },
                      React.createElement('i', { className: 'fa-solid fa-inbox' }),
                      React.createElement('p', null, 'Aucun échange trouvé pour ce filtre.')
                    );
                  }

                  const totalHist = historiquesFiltrés.length;
                  const totalHistPages = Math.max(1, Math.ceil(totalHist / HIST_PER_PAGE));
                  const safePage = Math.min(historiquePage, totalHistPages);
                  const histSlice = historiquesFiltrés.slice((safePage - 1) * HIST_PER_PAGE, safePage * HIST_PER_PAGE);

                  const pageNumbers = Array.from({ length: totalHistPages }, (_, i) => i + 1);

                  return React.createElement(React.Fragment, null,
                    histSlice.map((h, idx) => {
                      const typeCfg = getTypeConfig(h.type_echange);
                      const notesCfg = getNotesConfig(h.notes);
                      const sourceCfg = getSourceConfig(h.source);
                      const isLast = idx === histSlice.length - 1;

                      const dateStr = h.date_echange
                        ? new Date(h.date_echange).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—';

                      const formationsDisplay = h.formations_list && h.formations_list.length > 0
                        ? h.formations_list.map(f => f.intitule).join(', ')
                        : h.formations_noms || '';

                      return React.createElement('div', { key: h.id, className: 'echange-timeline-row' },
                        React.createElement('div', { className: 'echange-timeline-left' },
                          React.createElement('div', { className: 'echange-timeline-dot', style: { background: typeCfg.bg, border: `2px solid ${typeCfg.border}`, color: typeCfg.color } },
                            React.createElement('i', { className: `fa-solid ${typeCfg.icon}` })
                          ),
                          !isLast && React.createElement('div', { className: 'echange-timeline-line' })
                        ),
                        React.createElement('div', { className: 'echange-timeline-card', style: { borderLeft: `3px solid ${typeCfg.border}`, position: 'relative' } },
                          React.createElement('button', {
                            onClick: () => handleDeleteEchange(h.id),
                            disabled: deletingEchange,
                            style: {
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'transparent',
                              border: 'none',
                              color: '#cbd5e1',
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '6px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            },
                            onMouseEnter: (e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; },
                            onMouseLeave: (e) => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'transparent'; },
                            title: 'Supprimer cet échange'
                          },
                            React.createElement('i', { className: 'fa-solid fa-trash' })
                          ),
                          React.createElement('div', { className: 'echange-card-header' },
                            React.createElement('span', { className: 'echange-type-badge', style: { background: typeCfg.bg, color: typeCfg.color, border: `1px solid ${typeCfg.border}` } },
                              React.createElement('i', { className: `fa-solid ${typeCfg.icon}` }),
                              ' ',
                              typeCfg.label
                            )
                          ),
                          React.createElement('div', { className: 'echange-tags-container' },
                            formationsDisplay && React.createElement('span', { className: 'echange-tag echange-tag-formation' },
                              React.createElement('i', { className: 'fa-solid fa-graduation-cap' }),
                              ' Formations : ',
                              React.createElement('strong', null, formationsDisplay)
                            ),
                            sourceCfg && React.createElement('span', { className: `echange-tag ${sourceCfg.cls}` },
                              React.createElement('i', { className: 'fa-solid fa-tag' }),
                              ' Source : ',
                              React.createElement('strong', null, sourceCfg.label)
                            ),
                            notesCfg && React.createElement('span', { className: `echange-tag ${notesCfg.cls}` },
                              React.createElement('span', null, notesCfg.icon),
                              ' Niveau : ',
                              React.createElement('strong', null, notesCfg.label)
                            )
                          ),
                          React.createElement('div', { className: 'echange-date' },
                            React.createElement('i', { className: 'fa-regular fa-calendar' }),
                            React.createElement('span', null, dateStr),
                            h.utilisateur_nom && React.createElement(React.Fragment, null,
                              React.createElement('span', { style: { color: '#cbd5e1', margin: '0 4px' } }, '•'),
                              React.createElement('i', { className: 'fa-solid fa-user-tie', style: { color: '#94a3b8', fontSize: '11px' } }),
                              React.createElement('span', { style: { marginLeft: '3px' } }, h.utilisateur_nom)
                            )
                          ),
                          h.contenu && React.createElement('div', { className: 'echange-content' },
                            React.createElement('p', { className: 'echange-message' }, h.contenu)
                          )
                        )
                      );
                    }),
                    totalHistPages > 1 && React.createElement('div', { className: 'hist-pagination' },
                      React.createElement('span', { className: 'hist-pagination-info' },
                        `Affichage de ${(safePage - 1) * HIST_PER_PAGE + 1} à ${Math.min(safePage * HIST_PER_PAGE, totalHist)} sur ${totalHist} échanges`
                      ),
                      React.createElement('div', { className: 'hist-pagination-controls' },
                        React.createElement('button', {
                          className: 'hist-pg-btn',
                          disabled: safePage === 1,
                          onClick: () => setHistoriquePage(1)
                        }, React.createElement('i', { className: 'fa-solid fa-angles-left' })),
                        React.createElement('button', {
                          className: 'hist-pg-btn',
                          disabled: safePage === 1,
                          onClick: () => setHistoriquePage(p => Math.max(1, p - 1))
                        }, React.createElement('i', { className: 'fa-solid fa-angle-left' })),
                        pageNumbers.map(n =>
                          React.createElement('button', {
                            key: n,
                            className: `hist-pg-btn${safePage === n ? ' active' : ''}`,
                            onClick: () => setHistoriquePage(n)
                          }, n)
                        ),
                        React.createElement('button', {
                          className: 'hist-pg-btn',
                          disabled: safePage === totalHistPages,
                          onClick: () => setHistoriquePage(p => Math.min(totalHistPages, p + 1))
                        }, React.createElement('i', { className: 'fa-solid fa-angle-right' })),
                        React.createElement('button', {
                          className: 'hist-pg-btn',
                          disabled: safePage === totalHistPages,
                          onClick: () => setHistoriquePage(totalHistPages)
                        }, React.createElement('i', { className: 'fa-solid fa-angles-right' }))
                      ),
                      React.createElement('div', { className: 'hist-perpage-select' },
                        React.createElement('span', null, `${HIST_PER_PAGE} par page`)
                      )
                    )
                  );
                })()
              )
            ),
            React.createElement(ProspectRelances, {
              prospectId: p.id,
              prospectNom: p.prenom + ' ' + p.nom,
              formations_list: p.formations_list || [],
              onHistoriqueUpdated: function() { refreshDetail(p.id); }
            })
          )
        )
      ),
      React.createElement(RelanceModal, {
        isOpen: relanceModalOpen,
        onClose: () => setRelanceModalOpen(false),
        onSave: handleCreateRelance,
        title: "Programmer une relance",
        loading: relanceModalLoading,
        formations: p.formations_list || [],
      }),
      // ══════════════════════════════════════════════════════════════════════════════
      // MODAL ÉCHANGE SPONTANÉ - AVEC L'OPTION "PERDU"
      // ══════════════════════════════════════════════════════════════════════════════
      showEchangeModal && React.createElement('div', { className: 'relance-modal-overlay', onClick: () => setShowEchangeModal(false) },
        React.createElement('div', { className: 'relance-modal-card', onClick: e => e.stopPropagation() },
          React.createElement('div', { className: 'relance-modal-header' },
            React.createElement('div', { className: 'relance-modal-icon' },
              React.createElement('i', { className: 'fa-solid fa-comment-dots' })
            ),
            React.createElement('h3', { className: 'relance-modal-title' }, 'Nouvel échange'),
            React.createElement('button', { className: 'relance-modal-close', onClick: () => setShowEchangeModal(false) },
              React.createElement('i', { className: 'fa-solid fa-xmark' })
            )
          ),
          React.createElement('div', { className: 'relance-modal-body' },
            React.createElement('p', { className: 'ok-prompt-info' },
              React.createElement('i', { className: 'fa-solid fa-circle-info' }),
              ' Échange spontané pour ', React.createElement('strong', null, p.prenom, ' ', p.nom)
            ),
            React.createElement('div', { className: 'relance-field' },
              React.createElement('label', { className: 'relance-label' },
                React.createElement('i', { className: 'fa-solid fa-phone' }), ' Type d\'échange'
              ),
              React.createElement('div', { className: 'echange-type-radios' },
                ['appel', 'email', 'message', 'rdv', 'autre'].map(type =>
                  React.createElement('label', { key: type, className: `echange-type-radio ${echangeType === type ? 'active' : ''}` },
                    React.createElement('input', { type: 'radio', name: 'echange-type', value: type, checked: echangeType === type, onChange: () => setEchangeType(type) }),
                    type === 'appel' ? 'Appel' : type === 'email' ? 'Email' : type === 'message' ? 'WhatsApp' : type === 'rdv' ? 'Rendez-vous' : 'Autre'
                  )
                )
              )
            ),
            p.formations_list && p.formations_list.length > 0 && React.createElement('div', { className: 'relance-field' },
              React.createElement('label', { className: 'relance-label' },
                React.createElement('i', { className: 'fa-solid fa-graduation-cap' }), ' Formations concernées ', React.createElement('span', { className: 'optional-tag' }, 'optionnel')
              ),
              React.createElement('div', { className: 'formations-checkboxes' },
                p.formations_list.map(f =>
                  React.createElement('label', { key: f.id, className: 'checkbox-item' },
                    React.createElement('input', { type: 'checkbox', checked: echangeFormations.includes(f.id), onChange: () => {
                      setEchangeFormations(prev => prev.includes(f.id) ? prev.filter(id => id !== f.id) : [...prev, f.id]);
                    } }),
                    React.createElement('span', { className: 'checkbox-label' }, f.intitule)
                  )
                )
              ),
              echangeFormations.length > 0 && React.createElement('div', { className: 'selected-formations' },
                React.createElement('small', null,
                  React.createElement('i', { className: 'fa-solid fa-check' }),
                  ' ', echangeFormations.length, ' formation', echangeFormations.length > 1 ? 's' : '', ' sélectionnée', echangeFormations.length > 1 ? 's' : ''
                )
              )
            ),
            React.createElement('div', { className: 'relance-field' },
              React.createElement('label', { className: 'relance-label' },
                React.createElement('i', { className: 'fa-solid fa-fire' }), ' Niveau d\'intérêt ', React.createElement('span', { className: 'optional-tag' }, 'optionnel')
              ),
              // ✅ SELECT AVEC L'OPTION "PERDU" AJOUTÉE
              React.createElement('select', { className: 'relance-input', value: echangeNotes, onChange: e => setEchangeNotes(e.target.value) },
                React.createElement('option', { value: '' }, '— Non renseigné —'),
                React.createElement('option', { value: 'chaud' }, '🔥 Très intéressé'),
                React.createElement('option', { value: 'moyen' }, '🤔 Intéressé moyen'),
                React.createElement('option', { value: 'froid' }, '❄️ Pas intéressé'),
                React.createElement('option', { value: 'perdu' }, '🚫 Perdu')
              )
            ),
            React.createElement('div', { className: 'relance-field' },
              React.createElement('label', { className: 'relance-label' },
                React.createElement('i', { className: 'fa-regular fa-comment' }), ' Résumé de l\'échange ', React.createElement('span', { className: 'required-star' }, '*')
              ),
              React.createElement('textarea', {
                className: 'relance-textarea',
                rows: 3,
                placeholder: 'Ex: Le prospect demande les prix, souhaite s\'inscrire à la formation...',
                value: echangeContenu,
                onChange: e => setEchangeContenu(e.target.value)
              })
            )
          ),
          React.createElement('div', { className: 'relance-modal-footer' },
            React.createElement('button', { className: 'btn-relance-cancel', onClick: () => setShowEchangeModal(false), disabled: echangeSaving }, 'Annuler'),
            React.createElement('button', { className: 'btn-relance-save', onClick: handleAddEchange, disabled: echangeSaving || !echangeContenu.trim() },
              echangeSaving ?
                React.createElement(React.Fragment, null,
                  React.createElement('i', { className: 'fa-solid fa-spinner fa-spin' }),
                  ' Enregistrement…'
                ) :
                React.createElement(React.Fragment, null,
                  React.createElement('i', { className: 'fa-solid fa-check' }),
                  ' Enregistrer'
                )
            )
          )
        )
      ),
      React.createElement(SessionSelectionModal, {
        isOpen: showSessionSelection,
        onClose: () => setShowSessionSelection(false),
        onConfirm: handleSessionConfirm,
        formations: formationsForSession,
        prospectNom: `${detailTarget?.prenom} ${detailTarget?.nom}`
      }),
      React.createElement(DrawerPanel, { drawerOpen: drawerOpen, drawerMode: drawerMode, drawerTarget: drawerTarget, closeDrawer: closeDrawer, saveDrawer: saveDrawer, saving: saving, formRef: formRef, FORMATIONS: FORMATIONS }),
      React.createElement(DeleteModal, { showDeleteModal: showDeleteModal, deleteTarget: deleteTarget, closeDelete: closeDelete, confirmDelete: confirmDelete }),
      React.createElement(ConfirmConvertModal, { show: showConfirmConvert, detailTarget: detailTarget, selectedForms: selectedForms, convertData: convertData, FORMATIONS: FORMATIONS, onCancel: function() { setShowConfirmConvert(false); }, onConfirm: confirmConvert, converting: converting }),
      React.createElement(Toast, { toast: toast })
    );
  }
  
  // ══════════════════════════════════════════════════════════
  // PAGE LISTE
  // ══════════════════════════════════════════════════════════
  const hasActiveFilters = filterSource !== 'Toutes' || filterFormation !== 'Toutes' || filterScoreIA !== 'all' || !!filterDateDebut || !!filterDateFin;
  const clearAllFilters = () => { setFilterSource('Toutes'); setFilterFormation('Toutes'); setFilterScoreIA('all'); setFilterDateDebut(''); setFilterDateFin(''); setDateFilterOpen(false); setCurrentPage(1); };

  const getScoreFilterLabel = () => {
    const opt = SCORE_FILTER_OPTIONS.find(o => o.value === filterScoreIA);
    return opt ? opt.label : 'Tous les scores';
  };

  return React.createElement(Layout, null,
    React.createElement('div', { className: 'prsp-header' },
      React.createElement('div', { className: 'prsp-title' },
        React.createElement('i', { className: 'fa-solid fa-user-plus' }),
        ' Gestion des Prospects'
      ),
    ),
    React.createElement('div', { className: 'search-row' },
      React.createElement('div', { className: 'search-box' },
        React.createElement('i', { className: 'fa-solid fa-magnifying-glass' }),
        React.createElement('input', {
          placeholder: 'Rechercher par nom, prénom, email ou téléphone...',
          value: search,
          onChange: e => { setSearch(e.target.value); setCurrentPage(1); }
        })
      ),
      React.createElement('button', { className: 'btn btn-imp', onClick: () => setShowImport(true) },
        React.createElement('i', { className: 'fa-solid fa-file-import' }),
        ' Importer Excel'
      ),
      React.createElement('button', { className: 'btn btn-add', onClick: openAdd },
        React.createElement('i', { className: 'fa-solid fa-plus' }),
        ' Ajouter un prospect'
      )
    ),
    React.createElement('div', { className: 'filters-row' },
      React.createElement('div', { className: 'filters-left' },
  // ✅ BOUTON TRI EN PREMIER
  React.createElement('button', { className: `btn btn-sort ${sortAlpha ? 'active' : ''}`, onClick: () => setSortAlpha(v => !v) },
    React.createElement('i', { className: 'fa-solid fa-arrow-down-a-z' }),
    ' A → Z'
  ),
  React.createElement('select', { className: 'filter-sel', value: filterSource, onChange: e => { setFilterSource(e.target.value); setCurrentPage(1); } },
    React.createElement('option', { value: 'Toutes' }, 'Toutes les sources'),
    React.createElement('option', null, 'Facebook'),
    React.createElement('option', null, 'Instagram'),
    React.createElement('option', null, 'TikTok'),
    React.createElement('option', null, 'LinkedIn'),
    React.createElement('option', null, 'Google'),
    React.createElement('option', null, 'Site web'),
    React.createElement('option', null, 'Recommandation'),
    React.createElement('option', null, 'Appel entrant'),
    React.createElement('option', null, 'Autre')
  ),
  React.createElement(FormationFilterDropdown, {
    value: filterFormation,
    onChange: (val) => { setFilterFormation(val); setCurrentPage(1); },
    formations: FORMATIONS
  }),
  React.createElement('select', { className: 'filter-sel filter-score', value: filterScoreIA, onChange: e => { setFilterScoreIA(e.target.value); setCurrentPage(1); } },
    React.createElement('option', { value: 'all' }, 'Tous les scores'),
    React.createElement('option', { value: 'high' }, '🔥 Très intéressé (≥76%)'),
    React.createElement('option', { value: 'medium' }, '🟡 Intéressé moyen (40-75%)'),
    React.createElement('option', { value: 'low' }, '❄️ Pas intéressé (5-39%)'),
    React.createElement('option', { value: 'lost' }, '🚫 Perdu (<5%)')
  ),
        React.createElement('div', { className: 'date-filter-wrap' },
          React.createElement('button', {
            className: `btn btn-date-filter${(filterDateDebut || filterDateFin) ? ' active' : ''}`,
            onClick: () => setDateFilterOpen(v => !v)
          },
            React.createElement('i', { className: 'fa-regular fa-calendar' }),
            (filterDateDebut || filterDateFin) ? 'Date filtrée' : 'Filtrer par date',
            React.createElement('i', { className: `fa-solid fa-chevron-${dateFilterOpen ? 'up' : 'down'} date-chevron` })
          ),
          dateFilterOpen && React.createElement('div', { className: 'date-filter-panel' },
            React.createElement('div', { className: 'date-filter-row' },
              React.createElement('div', { className: 'date-filter-group' },
                React.createElement('label', null, React.createElement('i', { className: 'fa-regular fa-calendar-minus' }), ' Du'),
                React.createElement('input', {
                  type: 'date',
                  className: 'date-filter-input',
                  value: filterDateDebut,
                  max: filterDateFin || undefined,
                  onChange: e => { setFilterDateDebut(e.target.value); setCurrentPage(1); }
                })
              ),
              React.createElement('div', { className: 'date-filter-sep' }, '→'),
              React.createElement('div', { className: 'date-filter-group' },
                React.createElement('label', null, React.createElement('i', { className: 'fa-regular fa-calendar-plus' }), ' Au'),
                React.createElement('input', {
                  type: 'date',
                  className: 'date-filter-input',
                  value: filterDateFin,
                  min: filterDateDebut || undefined,
                  onChange: e => { setFilterDateFin(e.target.value); setCurrentPage(1); }
                })
              )
            ),
            (filterDateDebut || filterDateFin) && React.createElement('button', { className: 'date-filter-clear', onClick: () => { setFilterDateDebut(''); setFilterDateFin(''); setCurrentPage(1); } },
              React.createElement('i', { className: 'fa-solid fa-xmark' }),
              ' Effacer les dates'
            )
          )
        )
      ),
    ),
    React.createElement('div', { className: 'table-card' },
      React.createElement('div', { className: 'table-top' },
        React.createElement('strong', null, filtered.length),
        ' prospect', filtered.length !== 1 ? 's' : '', ' trouvé', filtered.length !== 1 ? 's' : '',
        hasActiveFilters && React.createElement('div', { className: 'active-filters' },
          filterSource !== 'Toutes' && React.createElement('span', { className: 'filter-badge' },
            React.createElement('i', { className: 'fa-solid fa-circle-dot', style: { fontSize: '9px' } }),
            ' Source : ', filterSource
          ),
          filterFormation !== 'Toutes' && React.createElement('span', { className: 'filter-badge' },
            React.createElement('i', { className: 'fa-solid fa-circle-dot', style: { fontSize: '9px' } }),
            ' Formation : ', FORMATIONS.find(f => String(f.id) === filterFormation)?.label || filterFormation
          ),
          filterScoreIA !== 'all' && React.createElement('span', { className: 'filter-badge filter-score-badge' },
            React.createElement('i', { className: 'fa-solid fa-chart-simple', style: { fontSize: '9px' } }),
            ' Score : ', getScoreFilterLabel()
          ),
          (filterDateDebut || filterDateFin) && React.createElement('span', { className: 'filter-badge' },
            React.createElement('i', { className: 'fa-regular fa-calendar', style: { fontSize: '10px' } }),
            ' ', filterDateDebut && filterDateFin ? `${filterDateDebut} → ${filterDateFin}` : filterDateDebut ? `À partir du ${filterDateDebut}` : `Jusqu'au ${filterDateFin}`
          ),
          React.createElement('button', { className: 'clear-filters', onClick: clearAllFilters },
            React.createElement('i', { className: 'fa-solid fa-xmark' }),
            ' Effacer'
          )
        )
      ),
      React.createElement(BulkActionBar, { count: selectedIds.size, onDelete: openBulkDelete, onClear: clearSelection }),
      loading ? React.createElement('div', { className: 'empty-state' },
        React.createElement('i', { className: 'fa-solid fa-spinner fa-spin', style: { fontSize: '28px', color: '#336699' } }),
        React.createElement('p', null, 'Chargement des prospects…')
      ) : apiError ? React.createElement('div', { className: 'empty-state' },
        React.createElement('i', { className: 'fa-solid fa-triangle-exclamation', style: { color: '#ef4444' } }),
        React.createElement('p', null, apiError),
        React.createElement('button', { className: 'btn btn-add', style: { marginTop: '12px' }, onClick: loadProspects },
          React.createElement('i', { className: 'fa-solid fa-rotate-right' }),
          ' Réessayer'
        )
      ) : React.createElement(React.Fragment, null,
        React.createElement('div', { className: 'table-wrap' },
          React.createElement('table', null,
            React.createElement('thead', null,
              React.createElement('tr', null,
                React.createElement('th', { className: 'th-check' },
                  React.createElement('input', {
                    type: 'checkbox',
                    className: 'row-checkbox',
                    checked: isAllPageSelected,
                    ref: el => { if (el) el.indeterminate = isSomePageSelected && !isAllPageSelected; },
                    onChange: toggleSelectAll,
                    title: isAllPageSelected ? 'Désélectionner tout' : 'Sélectionner tout'
                  })
                ),
                React.createElement('th', { style: { width: '32px' } }, '#'),
                React.createElement('th', null, 'Nom & Prénom'),
                React.createElement('th', null, 'Contact'),
                React.createElement('th', null, 'Formation souhaitée'),
                React.createElement('th', null, 'Source'),
                React.createElement('th', { className: 'th-score' }, 'Score IA'),
                React.createElement('th', { className: 'th-statut-genere' }, 'Statut IA'),
                React.createElement('th', null, 'Date'),
                React.createElement('th', { style: { textAlign: 'center' } }, 'Actions')
              )
            ),
            React.createElement('tbody', null,
              currentSlice.map((p, i) => {
                const isSelected = selectedIds.has(p.id);
                return React.createElement('tr', { key: p.id, className: isSelected ? 'row-selected' : '' },
                  React.createElement('td', { className: 'td-check', onClick: e => e.stopPropagation() },
                    React.createElement('input', {
                      type: 'checkbox',
                      className: 'row-checkbox',
                      checked: isSelected,
                      onChange: () => toggleSelect(p.id)
                    })
                  ),
                  React.createElement('td', { className: 'td-num' }, (currentPage - 1) * PER_PAGE + i + 1),
                  React.createElement('td', null,
                    React.createElement('div', { className: 'td-name' }, p.nom, ' ', p.prenom)
                  ),
                  React.createElement('td', null,
                    React.createElement('div', { className: 'td-sub' }, p.email),
                    React.createElement('div', { className: 'td-sub' }, p.tel)
                  ),
                  
React.createElement('td', null,
  React.createElement('div', { className: 'formations-list-compact' },
    (() => {
      // Récupérer les IDs des formations
      let formationIds = p.formations_souhaitees || p.formations || p.formations_ids || [];
      
      // Convertir en objets en utilisant FORMATIONS (state chargé depuis API)
      let formationsToShow = formationIds
        .map(id => {
          const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
          return FORMATIONS.find(f => f.id === numericId);
        })
        .filter(f => f);
      
      if (formationsToShow.length === 0) {
        return React.createElement('span', { className: 'no-formation' }, '—');
      }
      
      const displayFormations = formationsToShow.slice(0, 2);
      const remainingCount = formationsToShow.length - 2;
      
      return React.createElement(React.Fragment, null,
        displayFormations.map(f => 
          React.createElement('span', { key: f.id, className: 'formation-badge-compact' }, 
            f.label || f.intitule || '—'
          )
        ),
        remainingCount > 0 && React.createElement('span', { 
          className: 'extra-count-badge',
          key: 'extra',
          title: formationsToShow.slice(2).map(f => f.label || f.intitule).join(', ')
        }, 
          '+' + remainingCount
        )
      );
    })()
  )
),
                  React.createElement('td', null,
                    React.createElement('span', { className: 'src-tag' }, p.source)
                  ),
                  React.createElement('td', { className: 'td-score' },
                    React.createElement(ProgressScore, { score: p.scoreIA || 0, showBadge: true, showTooltip: true, size: 'small' })
                  ),
                  React.createElement('td', { className: 'td-statut-genere' }, (() => {
                    const sg = p.statutGenere;
                    if (!sg) return React.createElement('span', { style: { color: '#94a3b8', fontSize: '12px' } }, '—');
                    
                    let categorie;
                    if (p.scoreIA >= 76) categorie = 'Très intéressé';
                    else if (p.scoreIA >= 40) categorie = 'Intéressé moyen';
                    else if (p.scoreIA >= 5) categorie = 'Pas intéressé';
                    else categorie = 'Perdu';
                    
                    const cfg = STATUT_GENERE_CONFIG[categorie] || STATUT_GENERE_CONFIG['Pas intéressé'];
                    return React.createElement('span', {
                      className: 'badge badge-statut-genere',
                      style: { background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, display: 'inline-flex', alignItems: 'center', gap: '4px' },
                      title: sg
                    }, cfg.icon, ' ', sg);
                  })()),
                  React.createElement('td', { className: 'td-sub' }, p.date),
                  React.createElement('td', { className: 'td-actions' },
                    React.createElement('button', { className: 'act-btn act-detail', title: 'Voir le détail', onClick: () => openDetail(p.id) },
                      React.createElement('i', { className: 'fa-solid fa-eye' })
                    ),
                    React.createElement('button', { className: 'act-btn act-modif', title: 'Modifier', onClick: () => openEdit(p.id) },
                      React.createElement('i', { className: 'fa-solid fa-pen' })
                    ),
                    React.createElement('button', { className: 'act-btn act-suppr', title: 'Supprimer', onClick: () => openDelete(p.id) },
                      React.createElement('i', { className: 'fa-solid fa-trash' })
                    )
                  )
                );
              })
            )
          ),
          currentSlice.length === 0 && React.createElement('div', { className: 'empty-state' },
            React.createElement('i', { className: 'fa-solid fa-user-slash' }),
            React.createElement('p', null, 'Aucun prospect trouvé.')
          )
        ),
        totalPages > 1 && React.createElement('div', { className: 'pagination' },
          React.createElement('button', { className: 'pg-btn', disabled: currentPage === 1, onClick: () => setCurrentPage(1) },
            React.createElement('i', { className: 'fa-solid fa-angles-left' })
          ),
          React.createElement('button', { className: 'pg-btn', disabled: currentPage === 1, onClick: () => setCurrentPage(p => p - 1) },
            React.createElement('i', { className: 'fa-solid fa-angle-left' })
          ),
          Array.from({ length: totalPages }, (_, i) => i + 1).map(page =>
            React.createElement('button', { key: page, className: `pg-num ${currentPage === page ? 'active' : ''}`, onClick: () => setCurrentPage(page) }, page)
          ),
          React.createElement('button', { className: 'pg-btn', disabled: currentPage === totalPages, onClick: () => setCurrentPage(p => p + 1) },
            React.createElement('i', { className: 'fa-solid fa-angle-right' })
          ),
          React.createElement('button', { className: 'pg-btn', disabled: currentPage === totalPages, onClick: () => setCurrentPage(totalPages) },
            React.createElement('i', { className: 'fa-solid fa-angles-right' })
          )
        )
      )
    ),
    React.createElement(DrawerPanel, { drawerOpen: drawerOpen, drawerMode: drawerMode, drawerTarget: drawerTarget, closeDrawer: closeDrawer, saveDrawer: saveDrawer, saving: saving, formRef: formRef, FORMATIONS: FORMATIONS }),
    React.createElement(DeleteModal, { showDeleteModal: showDeleteModal, deleteTarget: deleteTarget, closeDelete: closeDelete, confirmDelete: confirmDelete }),
    React.createElement(BulkDeleteModal, { show: showBulkDeleteModal, count: selectedIds.size, onCancel: closeBulkDelete, onConfirm: confirmBulkDelete, deleting: bulkDeleting }),
    React.createElement(Toast, { toast: toast }),
    React.createElement(ImportProspectsModal, { isOpen: showImport, onClose: () => setShowImport(false), onSuccess: () => { loadProspects(); showToast('Import réussi !', 'success'); } })
  );
};

export default Prospects;